import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Button } from 'rebass';
import OutsideClickHandler from 'react-outside-click-handler';
import classNames from 'classnames';

import { PositionerV2 } from 'components/Positioner';
import Portal from 'components/Portal';
import InputSearch from 'components/InputSearch';
import Card from 'components/Card';
import helpers from 'utils/helpers';

import styles from './Combobox.module.css';

// Cap rendered options so very large item lists (e.g. ~15k) don't lock up
// the browser. The user narrows further by typing.
const MAX_VISIBLE_OPTIONS = 100;

/**
 * Combobox component. Kind of like a select, but with a search input.
 *
 * @param {{ value: string | number; label: string; disabled?: boolean; description?: string }[]} items List of items (options)
 * @param {string | number} value Currently selected value
 * @param {(value: string | number) => void} onChange Callback when the value changes
 * @param {string} [className]
 * @param {React.CSSProperties} [style]
 * @param {string} [ariaLabel] Aria label for the combobox
 * @param {string} [ariaLabelledBy] DOM id of an element labeling the combobox (alternative to ariaLabel)
 * @param {string} [id] ID for the combobox
 */
export function Combobox({ items = [], value, onChange, className, style, ariaLabel, ariaLabelledBy, id }) {
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(null);

    // Find the currently selected item’s label
    const label = useMemo(() => {
        const selectedItem = items.find(item => item.value === value);
        return selectedItem ? selectedItem.label : '';
    }, [items, value]);

    // Filter options based on the query (full match set — used for the result
    // count and truncation hint).
    const filteredOptions = useMemo(() => {
        const re = new RegExp(helpers.escapeRegExp(query), 'i');
        return items.filter(item => re.test(item.label));
    }, [items, query]);

    // Only render the first N to keep the DOM small. Keyboard navigation and
    // `aria-activedescendant` operate against this same slice.
    const options = useMemo(() => filteredOptions.slice(0, MAX_VISIBLE_OPTIONS), [filteredOptions]);
    const isTruncated = filteredOptions.length > options.length;

    // Closes the dropdown and returns keyboard focus to the trigger button so
    // the user's tab order picks up where it left off instead of falling back
    // to <body>. Used for "completed" close paths (Enter / click / Escape).
    // Tab and outside-click intentionally do NOT call this — the user is
    // already moving focus themselves.
    const closeAndRefocus = () => {
        setIsOpen(false);
        if (anchorRef.current) {
            anchorRef.current.focus({ preventScroll: true });
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (activeIndex === null) {
                setActiveIndex(0);
            } else {
                setActiveIndex(prev => Math.min(options.length - 1, prev + 1));
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (activeIndex === null) {
                setActiveIndex(options.length - 1);
            } else {
                setActiveIndex(prev => Math.max(0, prev - 1));
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const selectedOption = activeIndex !== null ? options[activeIndex] : options[0];

            if (selectedOption && !selectedOption.disabled) {
                onChange(selectedOption.value);
                setQuery('');
                closeAndRefocus();
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closeAndRefocus();
        } else if (e.key === 'Tab') {
            setIsOpen(false);
        }
    };

    const handleOptionClick = option => {
        if (option.disabled) return;
        onChange(option.value);
        setQuery('');
        closeAndRefocus();
    };

    const search = query => {
        setActiveIndex(null);
        setQuery(query);
    };

    useEffect(() => {
        if (activeIndex !== null && options[activeIndex]) {
            const activeElement = document.getElementById(`option-${options[activeIndex].value}`);
            if (activeElement) {
                activeElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex, options]);

    return (
        <>
            <Button
                id={id}
                width="100%"
                display="flex"
                className={classNames('flex items-center justify-between', className)}
                ref={anchorRef}
                variant="secondary-gray"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-describedby="combobox-trigger-instructions"
                onClick={() => setIsOpen(true)}
                style={style}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
            >
                {label || 'Select an option'} <FiChevronDown role="img" aria-label="Arrow down" style={{ margin: 0 }} />
            </Button>
            <div id="combobox-trigger-instructions" className="visually-hidden">
                Press Enter to open the combobox options.
            </div>
            {isOpen && (
                <Portal isOpen={isOpen}>
                    <PositionerV2 anchorRef={anchorRef}>
                        <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
                            <Card className={styles.card} style={{ width: anchorRef.current.offsetWidth }}>
                                <div className={styles.searchContainer}>
                                    <InputSearch
                                        ref={ref => ref && ref.focus({ preventScroll: true })}
                                        id="combobox-input"
                                        className="nice-underlined nice-dropdown-search"
                                        value={query}
                                        onChange={event => search(event.target.value)}
                                        aria-label="Search options"
                                        role="combobox"
                                        aria-owns="combobox-list"
                                        aria-controls="combobox-list"
                                        aria-activedescendant={
                                            activeIndex !== null && options[activeIndex]
                                                ? `option-${options[activeIndex].value}`
                                                : undefined
                                        }
                                        aria-autocomplete="list"
                                        aria-describedby="combobox-instructions"
                                        onKeyDown={handleKeyDown}
                                    />
                                    <div
                                        role="log"
                                        aria-live="polite"
                                        aria-atomic="false"
                                        aria-relevant="additions text"
                                        className="visually-hidden"
                                    >
                                        <div id="aria-results">
                                            {filteredOptions.length === 1
                                                ? `1 result found for search term "${query}".`
                                                : `${filteredOptions.length} results found for search term "${query}".`}
                                            {isTruncated &&
                                                ` Showing first ${options.length}. Keep typing to narrow the list.`}
                                        </div>
                                    </div>
                                    <div id="combobox-instructions" className="visually-hidden">
                                        {filteredOptions.length} result{filteredOptions.length === 1 ? '' : 's'} found.
                                        {isTruncated && ` Showing first ${options.length}.`} Start typing to search. Use
                                        the up and down arrow keys to navigate. Press Enter to select, or Escape to
                                        close.
                                    </div>
                                </div>
                                <div id="combobox-list" role="listbox" className={styles.list}>
                                    {options.length > 0 &&
                                        options.map((option, index) => (
                                            <div
                                                role="option"
                                                aria-selected={option.value === value}
                                                aria-disabled={option.disabled || undefined}
                                                id={`option-${option.value}`}
                                                key={option.value}
                                                tabIndex={-1}
                                                onClick={() => handleOptionClick(option)}
                                                onMouseEnter={() => setActiveIndex(index)}
                                                className={classNames(
                                                    styles.option,
                                                    index === activeIndex && styles.activeOption,
                                                    option.disabled && styles.disabledOption
                                                )}
                                            >
                                                {option.label}
                                                {option.description && (
                                                    <span className={styles.optionDescription}>
                                                        {option.description}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    {isTruncated && (
                                        <div className={styles.empty}>
                                            Showing first {options.length} of {filteredOptions.length}. Keep typing to
                                            narrow the list.
                                        </div>
                                    )}
                                    {filteredOptions.length === 0 && (
                                        <div className={styles.empty}>
                                            <strong>0 results</strong>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </OutsideClickHandler>
                    </PositionerV2>
                </Portal>
            )}
        </>
    );
}
